import { useEffect, useState } from 'react';
import AiqPaymentsService, { AiqPaymentRecord } from '../../services/aiq-payments.service';

const LIMIT = 20;

const formatDate = (iso?: string) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString();
};

const statusColors: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-800',
  paid: 'bg-blue-100 text-blue-800',
  consumed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  refunded: 'bg-gray-100 text-gray-600',
};

const AiqPayments = () => {
  const [payments, setPayments] = useState<AiqPaymentRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchPayments = async (offset: number = 0, append: boolean = false) => {
    try {
      setLoading(true);
      const res = await AiqPaymentsService.getAiqPayments(offset, LIMIT);
      const { payments: newPayments, total: newTotal } = res.data.data;
      setTotal(newTotal);
      setPayments((prev) => (append ? [...prev, ...newPayments] : newPayments));
    } catch (err) {
      console.error('AiqPayments fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments(0);
  }, []);

  return (
    <div className="p-6">
      <h2 className="text-2xl font-semibold mb-1">AIQ Payments</h2>
      <p className="text-sm text-gray-500 mb-4">
        {total} total record{total !== 1 ? 's' : ''}
      </p>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
            <tr>
              <th className="px-4 py-3 text-left">User</th>
              <th className="px-4 py-3 text-left">Tier</th>
              <th className="px-4 py-3 text-left">Amount</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Paid At</th>
              <th className="px-4 py-3 text-left">Consumed At</th>
              <th className="px-4 py-3 text-left">Session ID</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {payments.length === 0 && !loading && (
              <tr>
                <td colSpan={7} className="px-4 py-6 text-center text-gray-400">
                  No AIQ payments found.
                </td>
              </tr>
            )}
            {payments.map((p) => (
              <tr key={p._id} className="hover:bg-gray-50 transition">
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-800">{p.user?.name || '—'}</div>
                  <div className="text-xs text-gray-400">{p.user?.email || '—'}</div>
                </td>
                <td className="px-4 py-3 capitalize">{p.tier}</td>
                <td className="px-4 py-3">
                  ${(p.amount / 100).toFixed(2)} {p.currency?.toUpperCase()}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${statusColors[p.paymentStatus] || 'bg-gray-100 text-gray-600'}`}
                  >
                    {p.paymentStatus}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-500">{formatDate(p.paidAt)}</td>
                <td className="px-4 py-3 text-gray-500">{formatDate(p.consumedAt)}</td>
                <td className="px-4 py-3">
                  <span className="font-geistMono text-xs text-gray-400 truncate max-w-[140px] block" title={p.stripeSessionId}>
                    {p.stripeSessionId ? p.stripeSessionId.slice(0, 20) + '…' : '—'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {payments.length < total && (
        <button
          onClick={() => fetchPayments(payments.length, true)}
          disabled={loading}
          className="mt-4 rounded-lg border border-gray-300 px-5 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {loading ? 'Loading…' : `Load more (${total - payments.length} remaining)`}
        </button>
      )}

      {loading && payments.length === 0 && (
        <div className="mt-8 text-center text-gray-400">Loading…</div>
      )}
    </div>
  );
};

export default AiqPayments;
